import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Map } from 'maplibre-gl';
import { ascending } from 'd3';
import { GetServerSideProps } from 'next';
import { toast } from 'sonner';
import { TightLayout } from '@/components/layout/Layout';
import { MapContextProvider, useMap } from '@/hooks/useMap';
import { IMapOptions, IMapType } from '@/types/map';
import { IAreaGraph } from '@/types/explorer';
import useExplorerData from '@/hooks/useExplorerData';
import CircleChart from '@/components/ui/charts/CircleChart';
import { formatNumber } from '@/utils/explorer';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import AreaChart from '@/components/ui/charts/AreaChart';
import CustomTooltip from '@/components/ui/charts/CustomTooltip';
import { colors } from '@/utils/styles';
import { addLayer as addCoverageLayer } from '@/components/ui/map/layers/Coverage';
import * as CoverageData from '@/components/ui/map/sources/NewCoverageData';
import * as Toast from '@/components/ui/toast/Toast';

interface Props {
  country: string;
}

function MapPage({ country }: Props) {
  const [screenWidth, setScreenWidth] = useState<number | undefined>();
  const mapType: IMapType = 'coverage';
  const options: IMapOptions = {
    enableStyles: false,
    enableLocation: true,
  };
  const mapContextProps = {
    country,
    currentRegionName: '',
    screenWidth: screenWidth, // this defines what to show on the map by default
    mapType,
    options,
  };

  useEffect(() => {
    if (window) {
      setScreenWidth(window.innerWidth);
    }
  }, []);

  return (
    <TightLayout>
      {screenWidth && (
        <MapContextProvider {...mapContextProps}>
          <MapContent />
        </MapContextProvider>
      )}
    </TightLayout>
  );
}

function MapContent() {
  const mapMounted = useRef(false);
  const { map, areLayersReady, setAreLayersReady, mapContainerRef } = useMap();
  const { data } = useExplorerData();
  const stats = data?.stats;
  let uniqueKmMappedData: IAreaGraph[] = [];
  let totalKmMappedData: IAreaGraph[] = [];

  const totalUniqueKm =
    stats?.byDay?.[stats?.mostRecentDay]?.totalUniqueKm || 0;

  const totalKmMapped = stats?.byDay?.[stats?.mostRecentDay]?.totalKm || 0;

  const totalCoverage = Math.round((totalUniqueKm / 60000000) * 100);

  if (stats) {
    uniqueKmMappedData = Object.entries(stats?.byDay)
      .map(([date, value]) => ({
        date,
        value: value.totalUniqueKm,
      }))
      .sort((a, b) => ascending(a.date, b.date));

    totalKmMappedData = Object.entries(stats?.byDay)
      .map(([date, value]) => ({
        date,
        value: value.totalKm,
      }))
      .sort((a, b) => ascending(a.date, b.date));
  }
  const [toastId, setToastId] = useState<string | number>();
  const initializeData = useCallback(
    async (
      map: Map,
      setAreLayersReady: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      setAreLayersReady(false);

      await CoverageData.addSource(map);
      addCoverageLayer(map, {
        type: 'coverage',
      });

      setAreLayersReady(true);
      const id = toast.custom(
        t => (
          <Toast.Default
            title="You’re viewing the fastest growing map project in history!"
            containerClassName="w-[480px] md:flex-row flex-col"
            description={
              <>
                The Hivemapper Network coverage map reflects data processed
                since January 2024. Current coverage is added on a weekly basis.
                <br />
                <br />
                The{' '}
                <a
                  className="text-azure-700"
                  href="https://beemaps.com/product"
                  target="_blank"
                  rel="noreferrer">
                  Bee Maps APIs
                </a>{' '}
                provide access to imagery from the last year and map features
                since the inception of the project.
              </>
            }
            buttons={[
              {
                label: 'Dismiss',
                onClick: () => toast.dismiss(t),
                variant: 'secondary',
                size: 'fw',
              },
            ]}
          />
        ),
        { duration: Infinity, className: 'right-10 -bottom-4' },
      );
      setToastId(id);
    },
    [],
  );
  useEffect(() => {
    return () => {
      if (toastId) {
        toast.dismiss(toastId);
      }
    };
  }, [toastId]);

  useEffect(() => {
    if (map && !mapMounted.current) {
      initializeData(map, setAreLayersReady);
      mapMounted.current = true;
    }
  }, [map, initializeData, setAreLayersReady]);

  return (
    <>
      <div ref={mapContainerRef} className="absolute inset-0 flex size-full" />
      {stats && areLayersReady && (
        <div className="no-scrollbar absolute inset-x-4 bottom-12 top-auto flex max-h-[90%] max-w-full animate-fadeIn items-stretch gap-2 overflow-auto transition-opacity md:bottom-auto md:right-auto md:top-4 md:max-w-[200px] md:flex-col md:items-start md:gap-4 lg:max-w-xs">
          <Card className="h-auto w-full flex-1 rounded border-none bg-neutral-900 md:h-60 md:rounded-lg">
            <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
              <CardTitle>Unique KM Mapped</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-2 pt-0 md:p-6 md:pt-0">
              <p className="mb-1 text-base text-azure-400 md:text-2xl">
                {formatNumber(totalUniqueKm)}
              </p>
              <div className="hidden h-auto w-full flex-1 md:block md:h-36">
                <AreaChart
                  data={uniqueKmMappedData}
                  customTooltip={(props: any) => (
                    <CustomTooltip
                      line1={`${Math.round(
                        props?.payload[0]?.value || 0,
                      ).toLocaleString('en-US')} Unique KM Mapped`}
                      {...props}
                    />
                  )}
                />
              </div>
            </CardContent>
          </Card>
          <Card className="h-auto w-full flex-1 rounded border-none bg-neutral-900 md:h-60 md:rounded-lg">
            <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
              <CardTitle>Total KM Mapped</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-2 pt-0 md:p-6 md:pt-0">
              <p className="mb-1 text-base text-azure-400 md:text-2xl">
                {formatNumber(totalKmMapped)}
              </p>
              <div className="hidden h-auto w-full flex-1 md:block md:h-36">
                <AreaChart
                  data={totalKmMappedData}
                  customTooltip={(props: any) => (
                    <CustomTooltip
                      line1={`${Math.round(
                        props?.payload[0]?.value || 0,
                      ).toLocaleString('en-US')} Total KM Mapped`}
                      {...props}
                    />
                  )}
                />
              </div>
            </CardContent>
          </Card>
          <Card className="h-auto w-full flex-1 rounded border-none bg-neutral-900 md:h-60 md:rounded-lg">
            <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
              <CardTitle>Global Coverage</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-2 pt-0 md:p-6 md:pt-0">
              <p className="mb-1 text-base text-azure-400 md:hidden">
                {formatNumber(totalCoverage)}%
              </p>
              <CircleChart
                className="hidden h-auto md:block md:h-[180px]"
                data={[
                  {
                    fill: colors.azure[400],
                    value: totalCoverage,
                    name: 'total-coverage',
                  },
                ]}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

export default MapPage;

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const country = req.headers['x-country'] || 'unknown';
  return {
    props: {
      country,
    },
  };
};
